/**
 * ClaimSync Privacy Policy Page
 * Matching homepage design
 */

import React, { useEffect } from 'react';
import PublicShell from '../components/marketing/PublicShell';
import { Link } from 'react-router-dom';

export default function PrivacyPolicyPage() {
  const lastUpdated = 'December 24, 2024';

  useEffect(() => {
    // Scroll to top on page load
    window.scrollTo(0, 0);
    
    const header = document.querySelector('header');
    const handleScroll = () => {
      if (header) {
        header.style.boxShadow = window.pageYOffset > 100 ? '0 4px 20px rgba(0, 0, 0, 0.3)' : 'none';
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <PublicShell wide>

      {/* Hero Section */}
      <section className="pt-6 pb-12">
        <div className="mx-auto max-w-3xl px-5 text-center sm:px-8">
          <h1 className="font-heading text-4xl tracking-tight text-text-primary md:text-5xl">
            Privacy Policy
          </h1>
          <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.14em] text-text-dim">
            Last updated: {lastUpdated}
          </p>
        </div>
      </section>

      {/* Content */}
      <div className="mx-auto max-w-3xl px-5 py-12 sm:px-8 lg:py-16">
        <div className="border-t border-hairline pt-10">
          <div className="max-w-none">
            <Section title="1. Introduction">
              <p>
                The Figure Co. ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our business interruption claim calculation service.
              </p>
              <p>
                By using The Figure Co., you agree to the collection and use of information in accordance with this policy.
              </p>
            </Section>

            <Section title="2. Information We Collect">
              <h4 className="mt-4 mb-2 font-heading text-base text-text-primary">Personal Information</h4>
              <p>We may collect the following personal information:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Name and email address</li>
                <li>Account credentials</li>
                <li>Organization/company name</li>
                <li>Professional role and title</li>
              </ul>

              <h4 className="mt-4 mb-2 font-heading text-base text-text-primary">Business Data</h4>
              <p>To provide our services, we process:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Financial records and statements</li>
                <li>Business transaction data</li>
                <li>Claim-related documentation</li>
                <li>Calculation results and reports</li>
              </ul>

              <h4 className="mt-4 mb-2 font-heading text-base text-text-primary">Usage Information</h4>
              <p>We automatically collect:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>IP address and device information</li>
                <li>Browser type and version</li>
                <li>Pages visited and features used</li>
                <li>Time and date of access</li>
              </ul>
            </Section>

            <Section title="3. How We Use Your Information">
              <p>We use collected information to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Provide and maintain our claim calculation services</li>
                <li>Process and generate BI reports</li>
                <li>Authenticate users and manage accounts</li>
                <li>Send service-related communications</li>
                <li>Improve and optimize our platform</li>
                <li>Comply with legal obligations</li>
                <li>Detect and prevent fraud or abuse</li>
              </ul>
            </Section>

            <Section title="4. Data Sharing and Disclosure">
              <p>We may share your information with:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Service Providers:</strong> Third-party vendors who assist in operating our service</li>
                <li><strong>Team Members:</strong> Other users within your organization based on role permissions</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              </ul>
              <p className="mt-4">We do not sell your personal information to third parties.</p>
            </Section>

            <Section title="5. Data Security">
              <p>We implement appropriate technical and organizational measures to protect your data, including:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Encryption of data in transit and at rest</li>
                <li>Secure authentication through Clerk</li>
                <li>Role-based access controls</li>
                <li>Regular security assessments</li>
                <li>Secure cloud infrastructure</li>
              </ul>
            </Section>

            <Section title="6. Data Retention">
              <p>
                We retain your data for as long as your account is active or as needed to provide services. 
                You may request deletion of your data at any time by contacting us. 
                Some data may be retained longer if required by law or for legitimate business purposes.
              </p>
            </Section>

            <Section title="7. Your Rights">
              <p>Depending on your location, you may have the right to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Access your personal data</li>
                <li>Correct inaccurate data</li>
                <li>Delete your data</li>
                <li>Export your data (data portability)</li>
                <li>Opt-out of certain data processing</li>
                <li>Withdraw consent</li>
              </ul>
              <p className="mt-4">To exercise these rights, please contact us at the information provided below.</p>
            </Section>

            <Section title="8. Cookies and Tracking">
              <p>
                We use cookies and similar technologies to maintain sessions, remember preferences, and analyze usage. 
                You can control cookie settings through your browser. Disabling cookies may affect service functionality.
              </p>
            </Section>

            <Section title="9. Third-Party Services">
              <p>
                Our service integrates with third-party providers including Clerk (authentication), 
                cloud hosting providers, and analytics services. These providers have their own privacy policies 
                governing their use of your data.
              </p>
            </Section>

            <Section title="10. Children's Privacy">
              <p>
                The Figure Co. is not intended for use by individuals under 18 years of age.
                We do not knowingly collect personal information from children.
              </p>
            </Section>

            <Section title="11. Changes to This Policy">
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any changes 
                by posting the new policy on this page and updating the "Last updated" date.
              </p>
            </Section>

            <Section title="12. Contact Us">
              <p>If you have questions about this Privacy Policy or our data practices, please contact us:</p>
              <div className="mt-4 border-l-2 border-hairline pl-4">
                <p className="font-body text-sm leading-relaxed text-text-secondary">
                  <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-text-dim">Email</span> privacy@claimsync.io<br />
                  <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-text-dim">Address</span> The Figure Co., Inc.<br />
                  <Link to="/contact" className="underline decoration-[var(--color-border-strong)] underline-offset-2 hover:text-text-primary hover:decoration-current">Contact Form</Link>
                </p>
              </div>
            </Section>
          </div>
        </div>
      </div>
    </PublicShell>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-10">
      <h2 className="mb-4 font-heading text-2xl tracking-tight text-text-primary">{title}</h2>
      <div className="space-y-3 font-body text-sm leading-relaxed text-text-secondary">{children}</div>
    </section>
  );
}
